import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./AtlasPaywall.module.scss"

/**
 * AtlasPaywall — an original, self-contained, NON-privacy paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md). The prior fourteen paywalls
 * clustered on a centered hero + glowing card, dark/skeuomorphic art, and a
 * privacy/safety pitch. This one moves every axis:
 *   - Value frame:   PLACES / rediscover-by-location — "every photo pinned to
 *                    where you took it." No prior paywall pitched geography.
 *   - Layout:        a SPATIAL MAP — an illustrated atlas strewn with photo pins,
 *                    a drawn journey trail. No centered hero, no glowing card.
 *   - Interaction:   INPUT-DRIVEN spatial navigation — tap a pin, the map flies
 *                    focus to it and a place card reads out its memories.
 *   - Register:      WANDERLUST / warm discovery ("look how far you've ranged").
 *   - Art direction: LIGHT + CARTOGRAPHIC + PHOTOGRAPHIC. Paper ocean, sand
 *                    landmasses, a graticule, real Unsplash pins. No dark theme,
 *                    no particles, no glow.
 *
 * The pitch: CloudVault backs up every original in full quality and quietly
 * organizes your library by where each photo was taken — so your whole life
 * becomes a map you can wander. Yearly is the prominent, dockable price.
 */

interface Place {
  id: string
  name: string
  country: string
  continent: string
  img: string
  x: number // % across the map (west → east)
  y: number // % down the map (north → south)
  photos: number
  when: string
}

// A plausible journey, in order — the trail connects them in this sequence.
const PLACES: Place[] = [
  {
    id: "banff",
    name: "Banff",
    country: "Canada",
    continent: "North America",
    img: "/atlas/banff.jpg",
    x: 17,
    y: 26,
    photos: 61,
    when: "Aug 2022",
  },
  {
    id: "bigsur",
    name: "Big Sur",
    country: "USA",
    continent: "North America",
    img: "/atlas/bigsur.jpg",
    x: 12,
    y: 41,
    photos: 88,
    when: "Sep 2022",
  },
  {
    id: "oaxaca",
    name: "Oaxaca",
    country: "Mexico",
    continent: "North America",
    img: "/atlas/oaxaca.jpg",
    x: 19,
    y: 54,
    photos: 47,
    when: "Nov 2022",
  },
  {
    id: "iceland",
    name: "Reykjavík",
    country: "Iceland",
    continent: "Europe",
    img: "/atlas/iceland.jpg",
    x: 39,
    y: 17,
    photos: 134,
    when: "Mar 2023",
  },
  {
    id: "lisbon",
    name: "Lisbon",
    country: "Portugal",
    continent: "Europe",
    img: "/atlas/lisbon.jpg",
    x: 41,
    y: 42,
    photos: 96,
    when: "Jun 2023",
  },
  {
    id: "hallstatt",
    name: "Hallstatt",
    country: "Austria",
    continent: "Europe",
    img: "/atlas/hallstatt.jpg",
    x: 50,
    y: 33,
    photos: 52,
    when: "Jul 2023",
  },
  {
    id: "amalfi",
    name: "Amalfi",
    country: "Italy",
    continent: "Europe",
    img: "/atlas/amalfi.jpg",
    x: 52,
    y: 42,
    photos: 73,
    when: "Jul 2023",
  },
  {
    id: "santorini",
    name: "Santorini",
    country: "Greece",
    continent: "Europe",
    img: "/atlas/santorini.jpg",
    x: 57,
    y: 45,
    photos: 118,
    when: "Aug 2023",
  },
  {
    id: "marrakech",
    name: "Marrakech",
    country: "Morocco",
    continent: "Africa",
    img: "/atlas/marrakech.jpg",
    x: 43,
    y: 52,
    photos: 64,
    when: "Oct 2023",
  },
  {
    id: "capetown",
    name: "Cape Town",
    country: "South Africa",
    continent: "Africa",
    img: "/atlas/capetown.jpg",
    x: 55,
    y: 83,
    photos: 91,
    when: "Jan 2024",
  },
  {
    id: "kyoto",
    name: "Kyoto",
    country: "Japan",
    continent: "Asia",
    img: "/atlas/kyoto.jpg",
    x: 86,
    y: 38,
    photos: 142,
    when: "Apr 2024",
  },
  {
    id: "queenstown",
    name: "Queenstown",
    country: "New Zealand",
    continent: "Oceania",
    img: "/atlas/queenstown.jpg",
    x: 90,
    y: 86,
    photos: 79,
    when: "Feb 2025",
  },
]

const TOTAL_PHOTOS = PLACES.reduce((s, p) => s + p.photos, 0)
const TOTAL_CONTINENTS = new Set(PLACES.map((p) => p.continent)).size

// Softly organic landmass blobs behind the pins — abstract, cartographic.
const LANDMASSES = [
  "M6 18 C2 34 6 52 16 60 C24 66 30 58 30 46 C30 34 26 22 22 16 C16 8 9 8 6 18 Z",
  "M34 12 C30 22 32 34 40 40 C36 48 40 60 50 60 C60 60 66 50 63 40 C68 34 66 22 58 18 C50 14 42 6 34 12 Z",
  "M46 62 C42 70 46 84 55 88 C63 91 68 82 66 74 C64 66 54 56 46 62 Z",
  "M80 30 C74 36 76 48 84 50 C92 52 94 40 90 34 C88 30 84 26 80 30 Z",
  "M84 78 C80 84 84 92 90 92 C95 92 95 84 92 80 C90 77 86 74 84 78 Z",
]

function useCountUp(target: number, run: boolean, duration = 1.4) {
  const ref = useRef<HTMLSpanElement>(null)
  const reduce = useReducedMotion()
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!run) return
    if (reduce) {
      el.textContent = target.toLocaleString()
      return
    }
    const controls = animate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        el.textContent = Math.round(v).toLocaleString()
      },
    })
    return () => controls.stop()
  }, [target, run, duration, reduce])
  return ref
}

const AtlasPaywall = () => {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const savePercent =
    yearlyProduct && monthlyProduct
      ? Math.round(
          (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
        )
      : 79

  const [mounted, setMounted] = useState(false)
  const [selected, setSelected] = useState(7) // Santorini — a hero-y default
  const tourDone = useRef(false)

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60)
    return () => clearTimeout(t)
  }, [])

  // A brief auto-tour: after the pins drop, glide focus across a few places,
  // then rest — so the map demonstrates itself before the user touches it.
  useEffect(() => {
    if (!mounted || tourDone.current || reduce) return
    tourDone.current = true
    const stops = [3, 10, 7] // Iceland → Kyoto → Santorini
    const timers = stops.map((idx, i) =>
      setTimeout(() => setSelected(idx), 1200 + i * 1100),
    )
    return () => timers.forEach(clearTimeout)
  }, [mounted, reduce])

  const placesRef = useCountUp(PLACES.length, mounted, 1.2)
  const continentsRef = useCountUp(TOTAL_CONTINENTS, mounted, 1.2)
  const photosRef = useCountUp(TOTAL_PHOTOS, mounted, 1.6)

  const active = PLACES[selected]

  const trailPath = useMemo(() => {
    return PLACES.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(
      " ",
    )
  }, [])

  const handleClose = () => native.postNativeEvent("close")
  const buyYearly = () => {
    if (yearlyProduct) native.subscribe(yearlyProduct)
  }
  const buyMonthly = () => {
    if (monthlyProduct) native.subscribe(monthlyProduct)
  }

  const selectPlace = useCallback((i: number) => {
    tourDone.current = true // any tap cancels the auto-tour intent
    setSelected(i)
  }, [])

  return (
    <div className={styles.root}>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5 7 13 7 13s7-8 7-13c0-3.87-3.13-7-7-7z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <circle cx="12" cy="9" r="2.4" fill="currentColor" />
          </svg>
          <span className={styles.brandMark}>Atlas</span>
          <span className={styles.brandSub}>by CloudVault</span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <p className={styles.eyebrow}>Your photos, mapped</p>
        <h1 className={styles.title}>Everywhere you&rsquo;ve been.</h1>
        <p className={styles.counter} aria-live="polite">
          <span className={styles.stat}>
            <span ref={placesRef} className={styles.statNum}>
              0
            </span>{" "}
            places
          </span>
          <span className={styles.statDot} />
          <span className={styles.stat}>
            <span ref={continentsRef} className={styles.statNum}>
              0
            </span>{" "}
            continents
          </span>
          <span className={styles.statDot} />
          <span className={styles.stat}>
            <span ref={photosRef} className={styles.statNum}>
              0
            </span>{" "}
            photos
          </span>
        </p>
      </div>

      {/* THE MAP */}
      <div className={styles.mapWrap}>
        <div className={styles.map}>
          {/* graticule + landmasses */}
          <svg
            className={styles.terrain}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <g className={styles.grat}>
              {[20, 40, 60, 80].map((v) => (
                <line key={`h${v}`} x1="0" y1={v} x2="100" y2={v} />
              ))}
              {[20, 40, 60, 80].map((v) => (
                <line key={`v${v}`} x1={v} y1="0" x2={v} y2="100" />
              ))}
            </g>
            {LANDMASSES.map((d, i) => (
              <path key={i} className={styles.land} d={d} />
            ))}
          </svg>

          {/* the journey trail */}
          <svg
            className={styles.trail}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <motion.path
              d={trailPath}
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="0.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="1.6 1.8"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={
                mounted
                  ? { pathLength: 1, opacity: 0.55 }
                  : { pathLength: 0, opacity: 0 }
              }
              transition={{ duration: reduce ? 0 : 2.4, ease: "easeInOut" }}
            />
          </svg>

          {/* pins */}
          {PLACES.map((p, i) => {
            const isSel = i === selected
            return (
              <motion.button
                key={p.id}
                className={`${styles.pin} ${isSel ? styles.pinSel : ""}`}
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                onClick={() => selectPlace(i)}
                aria-label={`${p.name}, ${p.country} — ${p.photos} photos`}
                initial={{ opacity: 0, scale: 0, y: -14 }}
                animate={
                  mounted
                    ? { opacity: 1, scale: 1, y: 0 }
                    : { opacity: 0, scale: 0, y: -14 }
                }
                transition={{
                  delay: reduce ? 0 : 0.15 + i * 0.09,
                  type: "spring",
                  stiffness: 480,
                  damping: 20,
                }}
                whileTap={{ scale: 0.9 }}
              >
                <span className={styles.pinPhoto}>
                  <img src={p.img} alt="" loading="eager" />
                </span>
                <span className={styles.pinStem} />
                <span className={styles.pinLabel}>{p.name}</span>
              </motion.button>
            )
          })}

          {/* selected-place readout card */}
          <motion.div
            key={active.id}
            className={styles.placeCard}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 360, damping: 26 }}
          >
            <span
              className={styles.placeThumb}
              style={{ backgroundImage: `url(${active.img})` }}
            />
            <span className={styles.placeMeta}>
              <span className={styles.placeName}>
                {active.name}
                <span className={styles.placeCountry}>{active.country}</span>
              </span>
              <span className={styles.placeSub}>
                {active.photos} photos · {active.when}
              </span>
              <span className={styles.placeTag}>
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <path
                    d="M5 13l4 4L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Backed up in full quality
              </span>
            </span>
          </motion.div>
        </div>
      </div>

      {/* PLAN — a bottom sheet, yearly most prominent */}
      <div className={styles.sheet}>
        <div className={styles.sheetGrip} />
        <p className={styles.sheetLede}>
          Every place, every original — <b>kept</b> and mapped for you.
        </p>

        <div className={styles.priceRow}>
          <div className={styles.priceMain}>
            <span className={styles.priceYear}>{yearlyPrice}</span>
            <span className={styles.priceUnit}>/ year</span>
          </div>
          <div className={styles.priceAside}>
            <span className={styles.saveBadge}>Save {savePercent}%</span>
            <span className={styles.perMonth}>just {yearlyPerMonth}/mo</span>
          </div>
        </div>

        <button className={styles.cta} onClick={buyYearly}>
          Map my memories
        </button>

        <button className={styles.monthlyLink} onClick={buyMonthly}>
          or keep it monthly at {monthlyPrice}/mo
        </button>

        <p className={styles.legal}>
          Cancel anytime — your places and photos stay yours either way.
        </p>
        <div className={styles.legalLinks}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default AtlasPaywall
